/**
 * What a cat IS, once it has outlived the tab that made it.
 *
 * One record per cat, sixteen of them, and the same shape whether it came off
 * the disk, off the wire or out of the customizer.  There is exactly one
 * normalize() and everything goes through it, for the reason look.ts -- the
 * customizer's schema.js, before WS-05 moved it down beside this file -- gives:
 * nothing downstream should have to defend against a half-formed record, so
 * the defending happens once, here, at every edge.
 *
 * A hand-edited cats.json is exactly as untrusted as a socket.  That is not
 * paranoia about the operator -- it is that the file IS how a cat is repaired
 * when something goes wrong with it, and a repair that boots the server into a
 * crash loop is not a repair.
 */
import { normalize as normalizeLook, defaultLook } from "./look.js";
import { PLACES, FINDS, FIND_INDEX, freshWants } from "./happy.js";
import { NAME_MAX } from "./constants.js";
const FALLBACK_NAME = "Cat";
/**
 * Control characters, which would break a HUD line and a log line alike.
 *
 * Built from a string rather than written as a regex literal so the source
 * file itself contains no control bytes -- a file that has to be pasted,
 * diffed and hand-edited should not carry invisible characters of its own.
 */
const CONTROL = new RegExp("[\\u0000-\\u001f\\u007f-\\u009f]", "g");
/**
 * Clean a name.
 *
 * Runs of whitespace collapsed, trimmed, capped, and never empty.
 * Deliberately NOT filtered for content: this is a family game on a private
 * hostname with sixteen slots, and a word filter here would be theatre that
 * also rejects somebody's actual cat.
 */
export function cleanName(raw) {
    const s = String(raw ?? "")
        .replace(CONTROL, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, NAME_MAX);
    return s || FALLBACK_NAME;
}
/** What sits between a name and its ordinal: U+00B7 MIDDLE DOT.  One code
    unit, so "ghost·2" costs two of NAME_MAX and "ghost·10" three. */
const TAG_SEP = "·";
/**
 * A name nobody on the street already has.
 *
 * Two cats called "ghost" are one cat to every surface a player reads -- the
 * HUD, the ticker, the phrase bubbles -- so the SECOND join is tagged
 * server-side: "ghost", "ghost·2", "ghost·3"...  Never refused: a refused
 * stranger is this product's worst first minute (studio DR-AU, 2026-09-01:
 * suffix, one sentence for both titles).  Always inside NAME_MAX: the base is
 * truncated to make room for the tag rather than the tag pushed past the cap,
 * and a base left ending in a space is trimmed so the tag sits flush.  The
 * cost the ruling accepted is that the player did not choose the rendered
 * string.
 *
 * `clean` must already be a cleanName() result; `taken` is whatever the caller
 * counts as live -- world.js passes the sixteen beds, because an atticked cat
 * is not on the street and does not hold its name.
 */
export function uniqueName(clean, taken) {
    if (!taken.has(clean))
        return clean;
    for (let n = 2;; n++) {
        const tag = `${TAG_SEP}${n}`;
        const candidate = clean.slice(0, NAME_MAX - tag.length).trimEnd() + tag;
        if (!taken.has(candidate))
            return candidate;
    }
}
/**
 * A want vector, made safe.
 *
 * Length is pinned to PLACES: a record written before a place was added comes
 * back short, and the missing entries have to read as "wanted" rather than as
 * undefined -- a new place should be somewhere the cat has not been yet, which
 * is exactly what a want of 1 means.  A record written before a place was
 * REMOVED comes back long, and the tail is dropped.
 */
export function normalizeWants(raw) {
    const out = freshWants();
    if (!Array.isArray(raw))
        return out;
    for (let i = 0; i < PLACES.length; i++) {
        const v = Number(raw[i]);
        if (Number.isFinite(v))
            out[i] = Math.min(1, Math.max(0, v));
    }
    return out;
}
/**
 * The hunt tally, made safe: how many of each prey this cat has ever caught,
 * and how many catches it carried into the house as gifts.
 *
 * Counts, not currency -- happiness is paid through the wants at the moment
 * of the catch and the delivery (server/src/happiness.js), and this is only
 * the trophy shelf the HUD reads.  It still lives on the record because a
 * collection that resets on every server restart is not a collection.
 *
 * `gifts` can never legitimately exceed mouse + bird (every gift was first a
 * catch), but that is deliberately NOT enforced here: a hand-repaired file is
 * as untrusted as a socket, and the honest repair for an impossible tally is
 * clamping each number, not inventing a relationship between them and
 * guessing which one the operator meant.  The cap matches no gameplay limit
 * -- it exists so a corrupt file cannot park Infinity in a HUD line.
 */
const TALLY_CAP = 99999;
export function normalizeTally(raw) {
    const t = (raw && typeof raw === "object" ? raw : {});
    const n = (v) => {
        const x = Math.floor(Number(v));
        return Number.isFinite(x) && x > 0 ? Math.min(TALLY_CAP, x) : 0;
    };
    return { mouse: n(t.mouse), bird: n(t.bird), gifts: n(t.gifts) };
}
/**
 * The treasure collection, made safe: which of shared/happy.js's FINDS this
 * cat has ever dug out, as an array of find ids.
 *
 * The same trophy-shelf bargain as the tally, and it lives on the record for
 * the tally's reason: a collection that resets on every server restart is
 * not a collection -- "found them all" is precisely the promise the seek
 * mechanic makes to a completionist child.  Counts of RE-finds are
 * deliberately not kept: the discovery is the trophy, and "found the coin
 * 40 times" is a number that would only ever teach someone to farm it.
 *
 * Ids, not a bitmask and not indexes: a record written before a treasure was
 * added must read as "not found yet" (which unknown-id-dropped gives for
 * free in the other direction), and a record written before one was REMOVED
 * must shed it silently rather than crediting whatever treasure inherited
 * the index.  The array comes back sorted into FINDS order regardless of
 * the file's order, so the readout and the wire never depend on the order a
 * cat happened to explore in -- and a hand-edited duplicate collapses to
 * one, because a treasure is found or it is not.
 */
export function normalizeFinds(raw) {
    if (!Array.isArray(raw))
        return [];
    const got = new Set();
    for (const id of raw) {
        if (typeof id === "string" && id in FIND_INDEX)
            got.add(id);
    }
    return FINDS.filter((f) => got.has(f.id)).map((f) => f.id);
}
/**
 * Fill in what is missing and clamp what is out of range.
 *
 * `id` is the only field that cannot be invented: a record without one is not
 * a cat, it is a fragment, and the caller has to decide whether to mint an id
 * or drop it.  Everything else has an honest default.
 */
export function normalizeRecord(raw) {
    const r = (raw && typeof raw === "object" ? raw : {});
    const look = normalizeLook(r.look);
    const name = cleanName(r.name || look.name);
    // The look carries its own copy of the name because the customizer panel
    // edits it there.  They are kept equal here so no reader has to know which
    // one is authoritative.
    look.name = name;
    return {
        id: typeof r.id === "string" && r.id ? r.id.slice(0, 64) : null,
        name,
        look,
        wants: normalizeWants(r.wants),
        tally: normalizeTally(r.tally),
        finds: normalizeFinds(r.finds),
        // Seconds since epoch, both.  Used only for "which cat has been idle
        // longest" when the roster is full, so a bad value costs an eviction
        // order, not correctness.
        createdAt: Number.isFinite(r.createdAt) ? r.createdAt : 0,
        seenAt: Number.isFinite(r.seenAt) ? r.seenAt : 0,
    };
}
/** A brand new cat, wanting everything. */
export function newRecord(id, name, look, now) {
    return normalizeRecord({
        id,
        name,
        look: look || defaultLook(),
        wants: freshWants(),
        createdAt: now,
        seenAt: now,
    });
}
/**
 * The part of a record that goes on the wire.
 *
 * Not the wants, not the hunt tally and not the treasure collection -- all
 * three are the owner's business and travel in the owner-only messages
 * (`needs`, `finds`) -- and emphatically not the claim token, which is the one secret in the system and
 * has no reason to be in a message every client receives.  Leaving it out here
 * rather than stripping it at the send site means a new broadcast cannot leak
 * it by forgetting.
 */
export function wireRecord(rec) {
    return { id: rec.id, name: rec.name, look: rec.look };
}
